import { useState, useEffect, useCallback, useRef } from 'react';
import { DocumentSnapshot } from 'firebase/firestore';
import { fetchTransactions, FinanceTransaction } from '../service/financeService';

interface UseFinanceTransactionsOptions {
  companyId: string;
  type?: 'expense' | 'invoice' | 'manual' | 'adjustment';
  timeFilter?: 'week' | 'month' | 'half-year' | 'year' | 'all';
  pageSize?: number;
  cacheTime?: number; // in milliseconds, default 5 minutes
}

interface CacheData {
  transactions: FinanceTransaction[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
  timestamp: number;
}

const CACHE_TIME = 5 * 60 * 1000; // 5 minutes default

export const useFinanceTransactions = (options: UseFinanceTransactionsOptions) => {
  const { companyId, type, timeFilter = 'all', pageSize = 20, cacheTime = CACHE_TIME } = options;
  
  const [transactions, setTransactions] = useState<FinanceTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  
  // Cache storage
  const cacheRef = useRef<Map<string, CacheData>>(new Map());
  const cacheKey = `${companyId}-${type || 'all'}-${timeFilter}`;
  
  const loadTransactions = useCallback(async (reset = false) => {
    if (!companyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Check cache only on initial load
      if (reset) {
        const cached = cacheRef.current.get(cacheKey);
        const now = Date.now();
        
        if (cached && (now - cached.timestamp) < cacheTime) {
          // Use cached data
          setTransactions(cached.transactions);
          setLastDoc(cached.lastDoc);
          setHasMore(cached.hasMore);
          setLoading(false);
          return;
        }
      }
      
      // Fetch from server
      const result = await fetchTransactions(companyId, {
        type,
        timeFilter,
        pageSize,
        lastDoc: reset ? undefined : (lastDoc || undefined),
      });
      
      if (reset) {
        setTransactions(result.transactions);
        // Update cache
        cacheRef.current.set(cacheKey, {
          transactions: result.transactions,
          lastDoc: result.lastDoc,
          hasMore: result.hasMore,
          timestamp: Date.now(),
        });
      } else {
        setTransactions((prev) => [...prev, ...result.transactions]);
      }
      
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err: any) {
      setError(err);
      console.error('Error loading transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId, type, timeFilter, pageSize, cacheTime, cacheKey]);
  
  const loadMore = useCallback(async () => {
    if (!loading && hasMore && lastDoc && companyId) {
      setLoading(true);
      try {
        const result = await fetchTransactions(companyId, {
          type,
          timeFilter,
          pageSize,
          lastDoc,
        });
        
        setTransactions((prev) => [...prev, ...result.transactions]);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      } catch (err: any) {
        setError(err);
        console.error('Error loading more transactions:', err);
      } finally {
        setLoading(false);
      }
    }
  }, [loading, hasMore, lastDoc, companyId, type, timeFilter, pageSize]);
  
  const refresh = useCallback(() => {
    setLastDoc(null);
    setTransactions([]);
    cacheRef.current.delete(cacheKey);
    loadTransactions(true);
  }, [cacheKey, loadTransactions]);
  
  // Invalidate cache when a transaction is created/updated/deleted
  const invalidateCache = useCallback(() => {
    cacheRef.current.delete(cacheKey);
    refresh();
  }, [cacheKey, refresh]);
  
  useEffect(() => {
    loadTransactions(true);
  }, [companyId, type, timeFilter]);
  
  return {
    transactions,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    invalidateCache,
  };
};

